import { c } from 'ttag';

import { ContactMatchEmptyReason, ContactOnboardingStatus } from '../../../graphql/__generated__/schema';

/**
 * Step 1 reads two enumerations, and neither of them is re-derived here.
 *
 * `contactsByEmail` answers with **contacts** or with an **`emptyReason`**, and the
 * onboarding status of each contact comes from the view `contact_onboarding_status`
 *: a priority scale the API arbitrates once. This module turns those values into
 * what the operator reads; it never rebuilds them from `accounts.length` or from an
 * absent field. Two derivations of one fact drift, and the screen would end up
 * contradicting the query it is rendering.
 *
 * The wording is **ours**. The API's refusal strings are English and reach a French
 * operator as they stand, and an enumeration is not a message: what each value has
 * to say is the **gesture it calls for**, which is the only reason these values are
 * distinct at all.
 */

/** Why the search came back with nothing, and what to do about it. */
export interface LookupRefusal {
    /** The headline: what the query answered, in one sentence. */
    title: string;
    /** The gesture out. Never « check the address » unless re-reading it is the gesture. */
    body: string;
    tone: 'info' | 'warning' | 'danger';
}

/**
 * The three empty answers, separated by **the gesture**, which is the whole reason
 * the API carries three values rather than one.
 *
 * Attention, the middle one is the trap. « Des contacts existent, hors de votre périmètre »
 * means the address is **right**: suggesting a correction would send the operator
 * round in circles re-typing an address that was never the problem. It is the one
 * case where « vérifiez l'adresse » is actively harmful, so it is the one case where
 * the copy says the opposite in as many words.
 */
export const lookupRefusal = (reason: ContactMatchEmptyReason | null | undefined): LookupRefusal | null => {
    switch (reason) {
        case ContactMatchEmptyReason.NoContactForEmail:
            return {
                title: c('Onboarding lookup refusal').t`No ingested contact carries this address.`,
                body: c('Onboarding lookup refusal')
                    .t`Only known contacts can be onboarded: a person the source does not know has no account, no market and no perimeter, and would sign in to an empty portal. Re-read the address, or have the contact created in the source and search again once it has been ingested.`,
                tone: 'danger',
            };
        case ContactMatchEmptyReason.ContactsOutOfPerimeter:
            return {
                title: c('Onboarding lookup refusal').t`This address is known, but not on an account you administer.`,
                body: c('Onboarding lookup refusal')
                    .t`The address is the right one — do not correct it. Its contacts sit on accounts outside your portfolio, so neither their identity nor their company can be shown to you. Ask for the account to be added to your portfolio, or hand the onboarding over to whoever administers it.`,
                tone: 'warning',
            };
        case ContactMatchEmptyReason.NoAccountInPerimeter:
            return {
                title: c('Onboarding lookup refusal').t`You administer no account yet.`,
                body: c('Onboarding lookup refusal')
                    .t`No address will return anything until at least one account is entrusted to you — this is about your portfolio, not about the address you typed. Ask an administrator for a portfolio, then search again.`,
                tone: 'warning',
            };
        default:
            return null;
    }
};

/**
 * What a contact's onboarding status means **for the rest of this journey**.
 *
 * `selectable` is the load-bearing field, and it is the API's scale that decides it,
 * not a rule invented here:
 *
 * - `ELIGIBLE` and `ONBOARDED` let the journey continue. `ONBOARDED` is **not a
 *   refusal**: the mockup treats it as one, and that is the one place this screen
 *   knowingly departs from it. The person already has a portal account, so the
 *   journey stops being « créer » and becomes « ajouter des comptes »; the existing
 *   attachments keep their role and their perimeter, and `inviteUser` adds to them;
 * - the other four are refusals of **eligibility**, and each says which gesture lifts
 *   it. They stay listed with their reason rather than being dropped from the
 *   results: « vous ne pouvez pas onboarder ce contact » is not « cette adresse est
 *   inconnue », and only the first one is true.
 */
export interface ContactStatusReading {
    /** Short qualifier, printed beside the contact's name. */
    tag: string;
    /** What it means for the rest of the journey, and the gesture out when refused. Omitted where the tag says enough. */
    meaning?: string;
    /** Whether the journey may continue on this contact. */
    selectable: boolean;
    tone: 'info' | 'warning' | 'danger';
}

export const contactStatusReading = (status: ContactOnboardingStatus): ContactStatusReading => {
    switch (status) {
        case ContactOnboardingStatus.Eligible:
            return {
                tag: c('Onboarding contact status').t`Can be onboarded`,
                meaning: c('Onboarding contact status')
                    .t`Nothing on the source's side refuses this contact: the journey can go on to the accounts.`,
                selectable: true,
                tone: 'info',
            };
        case ContactOnboardingStatus.Onboarded:
            // A bifurcation, not a refusal. Blocking here would leave no way of
            // widening an existing person's perimeter, which is precisely one of the
            // journeys this screen serves.
            return {
                tag: c('Onboarding contact status').t`Already onboarded`,
                selectable: true,
                tone: 'info',
            };
        case ContactOnboardingStatus.LeftCompany:
            return {
                tag: c('Onboarding contact status').t`Left the company`,
                meaning: c('Onboarding contact status')
                    .t`The source declares this person as having left. Onboarding them would open an access to somebody who is no longer there — if they have in fact come back, the source has to say so first. Note that this refuses a new onboarding only: a person already onboarded keeps their access, whose revocation stays a traced human decision.`,
                selectable: false,
                tone: 'danger',
            };
        case ContactOnboardingStatus.NoAccount:
            return {
                tag: c('Onboarding contact status').t`Attached to no account`,
                meaning: c('Onboarding contact status')
                    .t`This contact carries no live attachment, so there is no perimeter to grant: the portal's data perimeter rests on accounts. Attach them to an account in the source, then search again. The address is known and correct — it is the attachment that is missing.`,
                selectable: false,
                tone: 'danger',
            };
        case ContactOnboardingStatus.Vanished:
            return {
                tag: c('Onboarding contact status').t`Gone from the source`,
                meaning: c('Onboarding contact status')
                    .t`This contact no longer exists on the source's side, so nothing should be onboarded on that basis — the person has no account there any more, hence no perimeter. Existing accesses are not revoked by this: that stays a traced human decision.`,
                selectable: false,
                tone: 'danger',
            };
        case ContactOnboardingStatus.NoEmail:
            // Attention, unreachable through this query, which searches **by** the address: a
            // contact without one cannot be found that way. Worded rather than folded
            // into a default, for the same reason as `NO_ACTIVE_MEMBERSHIP` on the
            // roles: a value rendered as nothing would be a silent refusal the day
            // another lookup (by business reference) does return it.
            return {
                tag: c('Onboarding contact status').t`No email address`,
                meaning: c('Onboarding contact status')
                    .t`This contact carries no email address in the source, and the address is what carries the invitation and becomes the sign-in identifier. Fill it in the source before onboarding them.`,
                selectable: false,
                tone: 'danger',
            };
    }
};

/** The full name as the source writes it, or null when it holds neither half. */
export const contactFullName = (contact: { firstName?: string | null; lastName?: string | null }): string | null =>
    [contact.firstName, contact.lastName].filter(Boolean).join(' ') || null;
